/**
 * TabDocumentsV2 — Documents simplifiés
 * ─────────────────────────────────────
 * Une seule question : « Quels documents dois-je transmettre ? »
 *
 * Design produit :
 *   • Une zone d'ajout unique et évidente (drag & drop OU clic).
 *   • Une liste plate de documents (nom, taille, date, téléchargement).
 *   • Un switch « visible client » directement sur la carte.
 *   • Pas de catégories. Pas de tags. Pas de moteur de recherche.
 *
 * Toutes les briques visuelles viennent de wyvio-ui.
 */
import { useRef, useState } from "react";
import {
  UploadCloud, FileText, Download, Eye, EyeOff, Loader2, Check,
} from "lucide-react";
import { Card, MicroLabel, Chip } from "@/components/wyvio-ui";
import SmartPhotos from "@/components/dossier/SmartPhotos";
import api from "@/lib/api";

const BASE = process.env.REACT_APP_BACKEND_URL || "";

const openDoc = async (doc) => {
  if (!doc.url) return;
  try {
    if (doc.url.startsWith("/api/files/")) {
      const res = await fetch(`${BASE}${doc.url}`, { credentials: "include" });
      if (!res.ok) throw new Error("download failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } else {
      window.open(doc.url, "_blank");
    }
  } catch (e) {
    console.error(e);
  }
};

const TabDocumentsV2 = ({ dossierId, documents = [], media = [], readOnly = false, onChange }) => {
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");

  const doUpload = async (file) => {
    if (!file || readOnly) return;
    setUploading(true);
    setError("");
    try {
      await api.uploadDocument(dossierId, file, file.name, true, null);
      await onChange?.();
    } catch (e) {
      setError(e.message || "Erreur lors du téléversement");
    } finally {
      setUploading(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (readOnly) return;
    const f = e.dataTransfer?.files?.[0];
    if (f) doUpload(f);
  };

  // Fichiers réels seulement (les "checklist" upcoming/pending sans url sont écartés)
  const files = (documents || []).filter((d) => d.url && d.status !== "upcoming");

  return (
    <div data-testid="tab-content-documents" className="space-y-8">
      {/* Photos intelligentes — la brique la plus utilisée */}
      <SmartPhotos
        dossierId={dossierId}
        media={media}
        readOnly={readOnly}
        onChange={onChange}
      />

      {/* Séparateur discret */}
      <div className="pt-2 border-t border-[#EEF2F5] dark:border-[#262626]" />

      {/* Documents (PDF, docs) */}
      <div className="space-y-6">
      {/* Zone d'ajout unique */}
      {!readOnly && (
        <div
          data-testid="documents-dropzone"
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          onClick={() => inputRef.current?.click()}
          role="button"
          tabIndex={0}
          className={[
            "cursor-pointer rounded-3xl border-2 border-dashed p-10",
            "flex flex-col items-center justify-center text-center gap-3",
            "transition-all duration-200 ease-out",
            dragOver
              ? "border-[#C8FF2F] bg-[#F3FCE1] dark:bg-[#1F2A0A]"
              : "border-[#EEF2F5] dark:border-[#262626] bg-white dark:bg-[#111111] hover:border-[#DCE1E7] dark:hover:border-[#3A3A3A]",
          ].join(" ")}
        >
          <input
            ref={inputRef}
            type="file"
            className="hidden"
            data-testid="documents-file-input"
            onChange={(e) => e.target.files?.[0] && doUpload(e.target.files[0])}
          />
          <div className="h-12 w-12 rounded-2xl bg-[#F3FCE1] dark:bg-[#1F2A0A] flex items-center justify-center text-[#4a6b0f] dark:text-[#C8FF2F]">
            {uploading ? (
              <Loader2 size={18} strokeWidth={2.25} className="animate-spin" />
            ) : (
              <UploadCloud size={18} strokeWidth={2.25} />
            )}
          </div>
          <div>
            <p className="text-[15px] font-semibold text-[#0A0A0A] dark:text-white">
              {uploading ? "Téléversement en cours…" : "Ajouter un document"}
            </p>
            <p className="mt-1 text-[12.5px] text-[#6B7280] dark:text-[#A3A3A3]">
              Glissez-déposez un fichier ou cliquez ici. Il sera automatiquement partagé avec le client.
            </p>
          </div>
        </div>
      )}

      {error && (
        <p data-testid="documents-error" className="text-[12.5px] text-[#991B1B]">
          {error}
        </p>
      )}

      {/* Liste */}
      <div>
        <MicroLabel>
          {files.length > 0
            ? `${files.length} document${files.length > 1 ? "s" : ""}`
            : "Aucun document"}
        </MicroLabel>

        {files.length === 0 ? (
          <p className="mt-3 text-[13px] text-[#6B7280] dark:text-[#A3A3A3]">
            Les documents que vous partagez apparaîtront ici.
          </p>
        ) : (
          <ul className="mt-3 space-y-2.5" data-testid="documents-list">
            {files.map((doc) => (
              <li key={doc.id}>
                <Card
                  className="px-4 py-3 flex items-center gap-4"
                  data-testid={`document-item-${doc.id}`}
                >
                  <div className="h-10 w-10 rounded-xl bg-[#F3F4F6] dark:bg-[#1A1A1A] flex items-center justify-center text-[#6B7280] dark:text-[#A3A3A3] shrink-0">
                    <FileText size={16} strokeWidth={2} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[14px] font-semibold text-[#0A0A0A] dark:text-white truncate">
                      {doc.name}
                    </p>
                    <p className="mt-0.5 text-[11.5px] text-[#6B7280] dark:text-[#A3A3A3]">
                      {doc.date_label}
                      {doc.size ? ` · ${doc.size}` : ""}
                      {doc.uploaded_by ? ` · ${doc.uploaded_by}` : ""}
                    </p>
                  </div>
                  <Chip
                    size="sm"
                    tone={doc.client_visible ? "accent" : "neutral"}
                    className="hidden sm:inline-flex"
                  >
                    {doc.client_visible ? (
                      <>
                        <Eye size={10} strokeWidth={2.25} /> Client
                      </>
                    ) : (
                      <>
                        <EyeOff size={10} strokeWidth={2.25} /> Interne
                      </>
                    )}
                  </Chip>
                  <button
                    onClick={() => openDoc(doc)}
                    data-testid={`document-download-${doc.id}`}
                    aria-label={`Télécharger ${doc.name}`}
                    className="h-9 w-9 rounded-full border border-[#EEF2F5] dark:border-[#262626] bg-white dark:bg-[#111111] flex items-center justify-center text-[#0A0A0A] dark:text-white hover:border-[#DCE1E7] dark:hover:border-[#3A3A3A] transition-colors"
                  >
                    <Download size={14} strokeWidth={2} />
                  </button>
                </Card>
              </li>
            ))}
          </ul>
        )}
      </div>
      </div>
    </div>
  );
};

export default TabDocumentsV2;
